import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEntitlements } from "@/hooks/useEntitlements";
import { ProviderCard } from "@/components/membership/Entitlement";
import { UpgradeModal } from "@/components/membership/UpgradeModal";
import { Loading } from "@/pages/_shared";
import { ShieldCheck, Info, FileText } from "lucide-react";

const CAT_ORDER = ["Business Credit", "Consumer Credit", "Personal Credit"];

export default function CreditReports() {
  const { ent, reload } = useEntitlements();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  if (!ent) return <Loading />;

  const feats = Object.values(ent.features);
  const groups = {};
  feats.forEach((f) => {
    if (!/credit/i.test(f.category)) return;
    (groups[f.category] ||= []).push(f);
  });
  const cats = Object.keys(groups).sort(
    (a, b) => (CAT_ORDER.indexOf(a) + 99) - (CAT_ORDER.indexOf(b) + 99)
  );
  const anyConnected = feats.some((f) => f.state === "connected");

  const onUpgrade = (f) => setModal({ feature: f.key, requiredPlan: f.required_plan, returnRoute: "/credit-reports" });
  const onConnect = () => navigate("/business-credit");

  return (
    <div className="mx-auto max-w-4xl animate-fade-up pb-10" data-testid="credit-reports-page">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Credit <span className="text-foil">reports</span></h1>
      <p className="mt-1 max-w-2xl text-[14px] text-slate-500">The business and personal scores lenders and vendors actually use. MMT shows real bureau data the moment a source is connected — never estimated or fabricated numbers.</p>

      {cats.length === 0 && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 text-[14px] text-slate-500" data-testid="cr-empty">
          No credit-report sources are configured for your account yet.
        </div>
      )}

      {cats.map((cat) => (
        <div key={cat} className="mt-6">
          <h2 className="font-head text-lg font-extrabold text-slate-900">{cat}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {groups[cat].map((f, i) => (
              <div key={f.key} className="animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <ProviderCard feature={f} onUpgrade={onUpgrade} onConnect={onConnect} onWaitlist={onConnect} onDetails={onConnect} />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5" data-testid="cr-insights">
        <div className="flex items-center gap-2 font-head text-[15px] font-bold text-slate-900"><FileText size={16} className="text-[#1a5fd0]" /> What to focus on</div>
        {anyConnected ? (
          <p className="mt-2 text-[13px] text-slate-500">Your top score factors will appear here, pulled directly from your connected bureaus.</p>
        ) : (
          <p className="mt-2 text-[13px] text-slate-500">Connect a bureau above to see the specific factors affecting your scores. Until then there's nothing to report — and we won't invent it.</p>
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="cr-education">
        <div className="flex items-center gap-2 font-head text-[14px] font-bold text-slate-900"><Info size={15} className="text-slate-500" /> How business credit scores work</div>
        <ul className="mt-2 space-y-1.5 text-[13px] leading-relaxed text-slate-600">
          <li>• <b>D&amp;B PAYDEX</b> (0–100) reflects how promptly you pay vendors and suppliers.</li>
          <li>• <b>Experian Intelliscore</b> and <b>Equifax Commercial</b> weigh tradelines, utilization, account age, and public records.</li>
          <li>• Adding reporting tradelines and paying early are the fastest honest ways to build.</li>
        </ul>
      </div>

      <p className="mt-8 flex items-center justify-center gap-2 text-center text-[12px] text-slate-400"><ShieldCheck size={14} /> Scores shown are pulled from connected bureaus only. Nothing here is estimated.</p>

      {modal && <UpgradeModal open onClose={() => { setModal(null); reload(); }} feature={modal.feature} requiredPlan={modal.requiredPlan} returnRoute={modal.returnRoute} ent={ent} />}
    </div>
  );
}
